 import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  FileText,
  Save,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Eye,
  Download,
  X
} from 'lucide-react';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import { StatusBadge } from '../../components/UI/StatusBadge';
import Portal from '../../components/UI/Portal';
import PdfViewer from '../../components/UI/PdfViewer';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import { confidentialAPI } from '../../services/api';
import FormSelect from '../../components/UI/FormSelect';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';


const OffboardingStep1 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep, reloadWorkflow } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [stepData, setStepData] = useState({
    resignation_date: '',
    resignation_reason: '',
    resignation_notes: '',
    resignation_letter_file: null,
    resignation_letter_filename: '',
    resignation_letter_uploaded: false
  });


  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };


  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 1);
      setStepData(initialData);
    }
  }, [workflow]);

  const handleSave = async () => {
    if (!stepData.resignation_date || !stepData.resignation_reason || (!stepData.resignation_letter_uploaded && !stepData.resignation_letter_file)) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      // Use the hook's saveStep method which handles both file uploads and regular updates
      const success = await saveStep(1, stepData, stepData.resignation_letter_file);
      if (success) {
        // Data is automatically reloaded by the hook
      }
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    // Auto-save before navigating to next step
    const hasUnsavedChanges =
      stepData.resignation_date ||
      stepData.resignation_reason ||
      stepData.resignation_notes ||
      stepData.resignation_letter_file ||
      stepData.resignation_letter_uploaded;

    if (hasUnsavedChanges && !workflow.step_1_completed) {
      // Validate required fields
      if (!stepData.resignation_date || !stepData.resignation_reason || (!stepData.resignation_letter_uploaded && !stepData.resignation_letter_file)) {
        toast.error('Please fill in all required fields before proceeding');
        return;
      }

      // Auto-save the data using the hook
      setSaving(true);
      const success = await saveStep(1, stepData, stepData.resignation_letter_file);
      setSaving(false);

      // FIX: Only navigate if save was successful
      if (!success) {
        toast.error('Please save the data before proceeding to the next step');
        return;
      }

      toast.success('Step 1 saved successfully');
    }

    // Navigate to next step only after successful save
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-2`);
  };

  // Helper function to get the correct final step number
  const getFinalStepNumber = () => {
    return workflow?.requires_ir21 ? 7 : 6;
  };

  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-sm text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 1: Resignation Details
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 1 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_1_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={1}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Resignation Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resignation Notice Date *
              </label>
              <input
                type="date"
                value={stepData.resignation_date}
                onChange={(e) => setStepData({...stepData, resignation_date: e.target.value})}
                className="input-admin"
                disabled={workflow.step_1_completed}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resignation Reason *
              </label>
              <FormSelect
                options={[
                  { value: '', label: 'Select reason...' },
                  { value: 'resignation', label: 'Voluntary Resignation' },
                  { value: 'termination', label: 'Termination' },
                  { value: 'retirement', label: 'Retirement' },
                  { value: 'contract_end', label: 'Contract End' },
                  { value: 'mutual_agreement', label: 'Mutual Agreement' },
                ]}
                value={stepData.resignation_reason}
                onChange={(val) => setStepData({...stepData, resignation_reason: val})}
                theme="admin"
                disabled={workflow.step_1_completed}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resignation Letter *
              </label>
              <div className="space-y-3">
                {stepData.resignation_letter_uploaded ? (
                  <div className="flex items-center justify-between px-4 py-2 bg-green-50 border border-green-200 rounded-full h-10">
                    <div className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm font-medium text-green-800">
                        {stepData.resignation_letter_filename}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => {
                          try {
                            console.log('🔍 View button clicked');
                            console.log('🔍 Workflow resignation letter fields:', {
                              url: workflow.resignation_letter_url,
                              file: workflow.resignation_letter_file,
                              filename: stepData.resignation_letter_filename
                            });
                            
                            // Use the API to get the file URL
                            const fileUrl = workflow.resignation_letter_url || confidentialAPI.buildDocumentViewUrl(workflow.resignation_letter_file || stepData.resignation_letter_filename);
                            console.log('🔍 Generated file URL:', fileUrl);
                            
                            // Set the document URL and open modal
                            setDocumentUrl(fileUrl);
                            setShowDocumentModal(true);
                          } catch (error) {
                            console.error('🔍 View error:', error);
                            toast.error('Unable to open file. Please try downloading instead.');
                          }
                        }}
                        className="flex items-center px-3 py-1.5 text-green-700 hover:text-green-900 hover:bg-green-100 text-sm font-medium rounded-md transition-colors"
                        title="View uploaded resignation letter"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            // Use the API to download the file
                            const response = await confidentialAPI.downloadResignationLetter(workflowId);
                            const blob = new Blob([response.data]);
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = stepData.resignation_letter_filename || 'resignation_letter';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error('Unable to download file');
                          }
                        }}
                        className="flex items-center px-3 py-1.5 text-green-700 hover:text-green-900 hover:bg-green-100 text-sm font-medium rounded-md transition-colors"
                        title="Download resignation letter"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setStepData({
                            ...stepData,
                            resignation_letter_file: file,
                            resignation_letter_filename: file.name,
                            resignation_letter_uploaded: true
                          });
                        }
                      }}
                      className="input-admin file:rounded-full file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                      disabled={workflow.step_1_completed}
                    />
                    {stepData.resignation_letter_filename && !stepData.resignation_letter_uploaded && (
                      <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border">
                        Selected: {stepData.resignation_letter_filename}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <input
                type="text"
                value={stepData.resignation_notes}
                onChange={(e) => setStepData({...stepData, resignation_notes: e.target.value})}
                className="input-admin"
                placeholder="Any additional notes about the resignation..."
                disabled={workflow.step_1_completed}
              />
            </div>
          </div>

          {!workflow.step_1_completed && workflow.workflow_status !== 'withdrawn' && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={() => navigate('/admin/employee-lifecycle')}
                className="btn btn-admin-outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Employee Lifecycle
              </button>

              <div className="flex space-x-3">
                {!workflow.step_1_completed && workflow.workflow_status !== 'withdrawn' && (
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="btn btn-danger"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Withdraw
                  </button>
                )}

                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn btn-admin-secondary"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Progress'}
                </button>

                <button
                  onClick={handleNext}
                  className="btn btn-admin-primary"
                >
                  Next Step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          )}

          {workflow.step_1_completed && (
            <div className="mt-8 flex justify-between">
              <button
                onClick={() => navigate('/admin/employee-lifecycle')}
                className="btn btn-admin-outline"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Employee Lifecycle
              </button>

              <div className="flex space-x-3">
                {!workflow.step_1_completed && workflow.workflow_status !== 'withdrawn' && (
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="btn btn-danger"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Withdraw
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="btn btn-admin-primary"
                >
                  Next Step
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={1}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
        />

        {/* Document Viewer Modal */}
        {showDocumentModal && (
          <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between gap-2 p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center min-w-0">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-xl font-bold text-gray-900 truncate">Resignation Letter</h3>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{stepData.resignation_letter_filename}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDocumentModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                  title="Close"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 p-6">
                <div className="w-full h-full border border-gray-200 rounded-lg overflow-hidden">
                  {(stepData.resignation_letter_filename || '').toLowerCase().endsWith('.pdf') ? (
                    <PdfViewer
                      src={documentUrl}
                      title="Resignation Letter Viewer"
                      fileName={stepData.resignation_letter_filename || 'resignation_letter.pdf'}
                    />
                  ) : (
                    <iframe
                      src={documentUrl}
                      className="w-full h-full"
                      title="Resignation Letter Viewer"
                      onError={() => {
                        toast.error('Unable to display document. Please try downloading instead.');
                        setShowDocumentModal(false);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  If the document doesn't load properly, try downloading it instead.
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={async () => {
                      try {
                        const response = await confidentialAPI.downloadResignationLetter(workflowId);
                        const blob = new Blob([response.data]);
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = stepData.resignation_letter_filename || 'resignation_letter';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error('Download error:', error);
                        toast.error('Unable to download file');
                      }
                    }}
                    className="btn btn-admin-secondary"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </button>
                  <button
                    onClick={() => setShowDocumentModal(false)}
                    className="btn btn-admin-outline"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          </Portal>
        )}
        </div>
      </div>
  );
};

export default OffboardingStep1;
